import { motion } from 'framer-motion';
import React from 'react';
import { FiArrowLeft, FiCreditCard, FiDatabase, FiDollarSign, FiLock, FiShield, FiTrendingUp } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer';
import Navbar from '../../components/Navbar';

const FinancialServices = () => {
  const solutions = [
    {
      title: "Digital Banking Platforms",
      description: "Modern, secure banking solutions with mobile-first design and real-time transaction capabilities.",
      icon: FiCreditCard,
      features: ["Mobile Banking", "Online Payments", "Account Management", "Digital Wallet Integration"]
    },
    {
      title: "Payment Processing Systems",
      description: "High-performance payment gateways and processing systems supporting multiple payment methods.",
      icon: FiDollarSign,
      features: ["Real-time Processing", "Multi-currency Support", "Fraud Prevention", "API Integration"]
    },
    {
      title: "Risk Management Solutions",
      description: "Advanced analytics and AI-powered tools for credit risk assessment and portfolio management.",
      icon: FiShield,
      features: ["Credit Scoring", "Portfolio Analytics", "Stress Testing", "Regulatory Reporting"]
    },
    {
      title: "Trading & Investment Platforms",
      description: "Sophisticated trading platforms with real-time market data and algorithmic trading capabilities.",
      icon: FiTrendingUp,
      features: ["Real-time Trading", "Market Analytics", "Portfolio Management", "Algorithmic Trading"]
    }
  ];

  const caseStudies = [
    {
      title: "Digital Transformation for Regional Bank",
      challenge: "Traditional bank needed modern digital banking platform to compete with fintech companies",
      solution: "Built comprehensive digital banking ecosystem with mobile app, web platform, and API infrastructure",
      results: ["300% increase in digital adoption", "50% reduction in operational costs", "95% customer satisfaction score"]
    },
    {
      title: "Payment Gateway for E-commerce",
      challenge: "High-volume e-commerce platform required scalable payment processing solution",
      solution: "Implemented microservices-based payment gateway with multi-currency and fraud detection",
      results: ["Processing $10M+ monthly transactions", "99.99% uptime achieved", "40% reduction in fraud losses"]
    }
  ];

  const compliance = [
    { name: "PCI DSS", desc: "Payment Card Industry Data Security Standard" },
    { name: "PSD2", desc: "Payment Services Directive 2" },
    { name: "GDPR", desc: "General Data Protection Regulation" },
    { name: "SOX", desc: "Sarbanes-Oxley Act" },
    { name: "Basel III", desc: "Banking Regulatory Framework" },
    { name: "AML/KYC", desc: "Anti-Money Laundering & Know Your Customer" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-indigo-100">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-20 relative overflow-hidden">
        <div className="relative h-96 md:h-[500px] bg-gradient-to-br from-indigo-600 to-purple-600">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/80 to-purple-600/60"></div>
          
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="space-y-6"
              >
                <div className="flex items-center space-x-4 text-white/80 text-sm font-body">
                  <Link 
                    to="/expertise"
                    className="flex items-center space-x-2 hover:text-white transition-colors duration-300"
                  >
                    <FiArrowLeft size={16} />
                    <span>Back to Expertise</span>
                  </Link>
                  <span>•</span>
                  <span className="px-3 py-1 bg-white/20 rounded-full font-accent tracking-wide">
                    Digital Banking & Fintech
                  </span>
                </div>
                
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <FiDollarSign className="text-white" size={32} />
                  </div>
                  <div>
                    <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight font-display tracking-tight">
                      Financial Services
                    </h1>
                    <p className="text-xl text-indigo-100 font-body font-light tracking-wide">
                      Transforming Finance Through Technology
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium mb-6 font-accent tracking-wide">
                Financial Innovation
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
                Revolutionizing
                <span className="block bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Financial Services
                </span>
              </h2>
              <p className="text-xl text-gray-600 mb-8 font-body font-light leading-relaxed">
                We partner with banks, fintech companies, and financial institutions to build secure, scalable, and 
                innovative solutions that transform how financial services are delivered and experienced.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4 bg-white rounded-2xl shadow-lg border border-indigo-100">
                  <div className="text-3xl font-bold text-indigo-600 font-display">40+</div>
                  <div className="text-sm text-gray-600 font-body">Financial Institutions</div>
                </div>
                <div className="text-center p-4 bg-white rounded-2xl shadow-lg border border-indigo-100">
                  <div className="text-3xl font-bold text-purple-600 font-display">10M+</div>
                  <div className="text-sm text-gray-600 font-body">Transactions Processed</div>
                </div>
                <div className="text-center p-4 bg-white rounded-2xl shadow-lg border border-indigo-100">
                  <div className="text-3xl font-bold text-indigo-600 font-display">100+</div>
                  <div className="text-sm text-gray-600 font-body">Compliance Projects</div>
                </div>
                <div className="text-center p-4 bg-white rounded-2xl shadow-lg border border-indigo-100">
                  <div className="text-3xl font-bold text-purple-600 font-display">99.9%</div>
                  <div className="text-sm text-gray-600 font-body">Security Uptime</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              {[
                { icon: FiLock, title: "Security First", desc: "Bank-grade security with multi-layer encryption and fraud prevention" },
                { icon: FiDatabase, title: "Regulatory Compliance", desc: "Ensuring adherence to PCI DSS, PSD2, and other regulations" },
                { icon: FiTrendingUp, title: "Scalable Architecture", desc: "Cloud-native solutions that scale with your business growth" }
              ].map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4 p-6 bg-white rounded-2xl shadow-lg border border-indigo-100">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 font-heading tracking-wide">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 font-body leading-relaxed">
                      {benefit.desc}
                    </p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              Regulatory
              <span className="block bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Compliance
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              We ensure your financial solutions meet all regulatory requirements and industry standards.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {compliance.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl border border-indigo-100"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-2 font-heading tracking-wide">
                  {item.name}
                </h3>
                <p className="text-gray-600 text-sm font-body leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-20 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              Financial
              <span className="block bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Solutions
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Comprehensive technology solutions designed for the modern financial services landscape.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="group p-8 bg-white rounded-3xl border border-indigo-100 hover:shadow-2xl transition-all duration-500"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <solution.icon className="text-white" size={28} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 font-heading tracking-wide">
                      {solution.title}
                    </h3>
                    <p className="text-gray-700 font-body leading-relaxed">
                      {solution.description}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {solution.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full"></div>
                      <span className="text-sm text-gray-700 font-body">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              Financial
              <span className="block bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Success Stories
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Real-world examples of how our financial solutions have transformed banking and fintech operations.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-indigo-50 to-purple-50 p-8 rounded-3xl border border-indigo-100"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4 font-heading tracking-wide">
                  {study.title}
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2 font-heading tracking-wide">Challenge</h4>
                    <p className="text-gray-600 font-body leading-relaxed">{study.challenge}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2 font-heading tracking-wide">Solution</h4>
                    <p className="text-gray-600 font-body leading-relaxed">{study.solution}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3 font-heading tracking-wide">Results</h4>
                    <div className="space-y-2">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full"></div>
                          <span className="text-gray-700 font-body">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-indigo-600 to-purple-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-white/5"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-display tracking-tight">
              Ready to Transform Your Financial Services?
            </h2>
            <p className="text-xl text-indigo-100 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Let's discuss how our financial expertise can help you build secure, scalable, and innovative financial solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-indigo-600 px-8 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 font-accent tracking-wide"
                >
                  Start Your Financial Project
                </motion.button>
              </Link>
              <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-indigo-600 transition-all duration-300 font-accent tracking-wide"
              >
                Schedule Consultation
              </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FinancialServices; 